import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import {
  useEngagementPrefill,
  UploadedFile,
} from "../../../hooks/useEngagementPrefill";
import { AIModalState } from "../../../enums/CompassAI";
import { HeroKind } from "../../../enums/Modal";
import { MESSAGES } from "../../../messages";
import { AIPrefillEngagementResponse } from "../../../types/compassAITypes";
import { Input } from "antd";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  PreserveSpace,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { FileDropZone } from "../FileUpload";
import { Icon, IconContainer } from "../../visuals/styledComponents";
import { GradientProgressBar } from "../FileUpload/GradientProgressBar";
import { DeleteItem } from "../../tables/DeleteItem";
import { greyText, darkGreen, errorRed } from "../../../styles";

// Assets
import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import engagementExtractionBg from "../../../assets/images/engagement_record_generation.png";
import aiJobRunningAnimation from "../../../assets/svg/ai_job_running_animation.svg";
import fileIcon from "../../../assets/svg/file_icon.svg";

const { TextArea } = Input;

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UploadedFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const UploadedFileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: ${greyText};
  flex-shrink: 0;
`;

const FileStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${greyText};
  margin-bottom: 8px;
`;

const TextInputWrapper = styled.div`
  margin-top: 4px;

  .ant-input {
    font-size: 13px;
    font-family: "Apercu", sans-serif;
  }
`;

const CharCount = styled.div<{ $isNearLimit: boolean }>`
  font-size: 11px;
  color: ${({ $isNearLimit }): string => ($isNearLimit ? "#ff4d4f" : greyText)};
  text-align: right;
  margin-top: 4px;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e8e8e8;
  }

  span {
    font-size: 12px;
    color: ${greyText};
    text-transform: uppercase;
  }
`;

// =============================================================================
// MODAL CONFIG
// =============================================================================

const messages = MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL;

type ModalContext = { dotCounter: number };

const PREFILL_MODAL_CONFIG = defineModalConfig<AIModalState, ModalContext>({
  title: messages.MODAL_TITLE,
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    [AIModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.IDLE,
        description: messages.DESCRIPTION.IDLE,
      },
    },
    [AIModalState.UPLOADING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.UPLOADING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.UPLOADED]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.UPLOADED,
        description: messages.DESCRIPTION.UPLOADED,
      },
    },
    [AIModalState.EXTRACTING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.EXTRACTING,
        description: messages.DESCRIPTION.EXTRACTING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.SUCCESS]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.SUCCESS,
        description: messages.DESCRIPTION.SUCCESS,
      },
    },
    [AIModalState.ERROR]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ERROR,
        titleColor: errorRed,
        accentColor: errorRed,
        description: messages.DESCRIPTION.ERROR,
      },
    },
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface EngagementPrefillModalProps {
  /** Whether the modal is visible */
  show: boolean;
  /** Handler to close the modal */
  onHide: () => void;
  /** Selected company ID (required) */
  companyId: string;
  /** Callback when prefill completes successfully */
  onSuccess?: (result: AIPrefillEngagementResponse) => void;
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const UploadedFileItem: React.FC<{
  file: UploadedFile;
  onRemove: () => void;
}> = ({ file, onRemove }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <UploadedFileCard>
      <FileInfo>
        <IconContainer size={32} p={1} m={1}>
          <Icon src={fileIcon} alt="file" />
        </IconContainer>
        <FileName>{file.file.name}</FileName>
        <FileSize>{formatFileSize(file.file.size)}</FileSize>
      </FileInfo>
      <FileStatus>
        {file.isUploading ? (
          <GradientProgressBar isLoading={true} />
        ) : file.error ? (
          <CompassParagraph fontSize={12} color="#ff4d4f">
            Failed
          </CompassParagraph>
        ) : null}
        <DeleteItem id="file" isDeletable={true} onDelete={onRemove} />
      </FileStatus>
    </UploadedFileCard>
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

const MAX_PROMPT_LENGTH = 50000;

export const EngagementPrefillModal: React.FC<EngagementPrefillModalProps> = ({
  show,
  onHide,
  companyId,
  onSuccess,
}) => {
  const [promptText, setPromptText] = useState("");
  const [dotCounter, setDotCounter] = useState(0);

  const {
    state,
    uploadedFiles,
    addFile,
    removeFile,
    startPrefill,
    reset,
    isUploading,
    isExtracting,
    canAddMoreFiles,
  } = useEngagementPrefill();

  // Dot animation for loading states
  useEffect(() => {
    if (isUploading || isExtracting) {
      const interval = setInterval(() => {
        setDotCounter((prev) => (prev + 1) % 4);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isUploading, isExtracting]);

  // Handle extraction with success callback
  const handleExtract = async (): Promise<void> => {
    const result = await startPrefill(companyId, promptText);
    if (result && onSuccess) {
      onSuccess(result);
      handleClose();
    }
  };

  // Reset on close
  const handleClose = useCallback(() => {
    reset();
    setPromptText("");
    setDotCounter(0);
    onHide();
  }, [reset, onHide]);

  // File selection handler
  const handleFileSelect = useCallback(
    async (file: File) => {
      await addFile(file);
    },
    [addFile],
  );

  // Modal config
  const { modalProps, isProcessing } = useCompassModal({
    config: PREFILL_MODAL_CONFIG,
    state,
    context: { dotCounter },
  });

  // Check if we can extract (have files or text)
  const hasContent =
    uploadedFiles.some((f) => f.isUploaded) || promptText.trim().length > 0;
  const canExtract = hasContent && !isUploading && !isExtracting;

  // Footer content based on state
  const renderFooter = (): React.ReactNode => {
    if (state === AIModalState.SUCCESS) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Done"
          hideSubmit
          showDivider={false}
        />
      );
    }

    if (state === AIModalState.ERROR) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Close"
          submitText="Try Again"
          onSubmit={(): void => {
            reset();
            setPromptText("");
          }}
          showDivider={false}
        />
      );
    }

    return (
      <ModalFooter
        onClose={handleClose}
        closeText="Cancel"
        submitText={messages.EXTRACT_BUTTON}
        onSubmit={handleExtract}
        isLoading={isProcessing}
        submitDisabled={!canExtract}
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>
        <PreserveSpace
          hidden={
            state === AIModalState.EXTRACTING || state === AIModalState.SUCCESS
          }
        >
          <ContentWrapper>
            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div>
                <SectionLabel>
                  Uploaded Files ({uploadedFiles.length}/5)
                </SectionLabel>
                <UploadedFilesContainer>
                  {uploadedFiles.map((file, index) => (
                    <UploadedFileItem
                      key={`${file.file.name}-${index}`}
                      file={file}
                      onRemove={(): void => removeFile(index)}
                    />
                  ))}
                </UploadedFilesContainer>
              </div>
            )}

            {/* File Drop Zone - always visible if can add more */}
            {canAddMoreFiles && (
              <FileDropZone
                file={null}
                isUploading={false}
                onFileSelect={handleFileSelect}
                fileTypesDescription={messages.FILE_TYPES}
                fileTypeLabel="document"
                accept=".pdf,.docx,.xlsx,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                showFileInfo={false}
              />
            )}

            {/* Or Divider */}
            <OrDivider>
              <span>or</span>
            </OrDivider>

            {/* Text Input */}
            <TextInputWrapper>
              <SectionLabel>Paste Text</SectionLabel>
              <TextArea
                value={promptText}
                onChange={(e): void => setPromptText(e.target.value)}
                placeholder={messages.TEXT_PLACEHOLDER}
                rows={5}
                maxLength={MAX_PROMPT_LENGTH}
                disabled={isExtracting}
              />
              <CharCount
                $isNearLimit={promptText.length > MAX_PROMPT_LENGTH * 0.9}
              >
                {promptText.length.toLocaleString()} /{" "}
                {MAX_PROMPT_LENGTH.toLocaleString()}
              </CharCount>
            </TextInputWrapper>
          </ContentWrapper>
        </PreserveSpace>
      </ModalContent>
    </CompassModal>
  );
};
